import { Auth0Provider } from '@auth0/auth0-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { createClient, Provider, defaultExchanges } from 'urql';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ReactElement, useEffect } from 'react';
import { CartProvider } from 'CartContext';
import ButtonGenerator from 'pages/ButtonGenerator';
import CategoryTree from 'pages/CategoryTree';
import Checkout from 'components/Checkout';
import ErrorPage from 'components/imedia/components/ErrorPage';
import ExchangeSiteMap from 'components/imedia/components/ExchangeSiteMap';
import Store from 'components/imedia/Store';
import OrdersAndDC from 'pages/OrdersAndDC';
import Privacy from 'components/imedia/components/Privacy';
import ProductPage from 'pages/ProductPage';
import ProductList from 'pages/ProductList';
import ShoppingCartPage from 'pages/ShoppingCartPage';
import ShoppingCartQuantity from 'pages/ShoppingCartQuantity';
import SkuExample from 'pages/SkuExample';
import ShopBlogDetails from 'pages/shop-blog-details';
import ShopHomePage from 'pages/shop-home-page';
import ShopBlogExplorer from 'pages/shop-blog-explorer';
import ScrollToTop from 'utils/ScrollToTop';
import Contactus from 'pages/contact-us';
// import FAQ from 'pages/shop-home-page/faq';
import './index.scss';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'primereact/resources/themes/saga-purple/theme.css';
import CheckoutComplete from 'components/CheckoutComplete';
import CartIconGenerator from 'pages/CartIconGenerator';
import DonatePCF from 'pages/DonatePCF';
import CollectionPage from 'pages/Collection';
import CollectionIndex from 'pages/Collections';
import { Head } from 'components/head/Head';
import ContentPage from 'pages/ContentPage';
import { getMarketContent } from 'utils/marketContent';
import TokenLanding from 'pages/token-unit-crowdpoint/TokenLanding';
// import './utils/marketContent';

const magentoUrl = process.env.REACT_APP_MAGENTO_URL;
const magentoGraphql = `${magentoUrl}/graphql`;
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const stripePromise = loadStripe(stripePublishableKey);

export default function App(): ReactElement {
  const client = createClient({
    url: magentoGraphql,
    exchanges: [...defaultExchanges],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const market = getMarketContent();
  const data = market.market;

  return (
    <>
      <Head
        link="/"
        title="crwd market"
        addDarkModeStyle
        keywords="blockchain marketplace, blockchain ecosystem, crwd market"
      />
      <Elements stripe={stripePromise}>
        <Auth0Provider
          audience={process.env.REACT_APP_AUTH0_AUDIENCE || ''}
          cacheLocation="localstorage"
          clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
          domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
          useRefreshTokens
        >
          <Provider value={client}>
            <CartProvider>
              <BrowserRouter>
                <ScrollToTop />
                <Routes>
                  <Route
                    path="button-generator"
                    element={<ButtonGenerator />}
                  />
                  <Route
                    path="cart-icon-generator"
                    element={<CartIconGenerator />}
                  />
                  <Route path="vendor-tools" element={<ButtonGenerator />} />

                  <Route
                    path="cart-quantity"
                    element={<ShoppingCartQuantity />}
                  />
                  <Route path="cart" element={<ShoppingCartPage />} />
                  <Route path="category-tree" element={<CategoryTree />} />
                  <Route path="category/*" element={<ProductList />} />
                  <Route path="collection/*" element={<CollectionPage />} />
                  <Route path="collections" element={<CollectionIndex />} />
                  <Route path="donate-pcf" element={<DonatePCF />} />
                  <Route path="checkout" element={<Checkout />} />
                  <Route
                    path="checkout-complete"
                    element={<CheckoutComplete />}
                  />
                  <Route path="digital-content" element={<OrdersAndDC />}>
                    <Route path=":productSku" element={<OrdersAndDC />}>
                      <Route path=":itemId" element={<OrdersAndDC />} />
                    </Route>
                  </Route>
                  <Route
                    path="exchange-sitemap"
                    element={<ExchangeSiteMap />}
                  />
                  <Route path="sku-example" element={<SkuExample />} />
                  <Route path="orders" element={<OrdersAndDC />} />
                  <Route path="privacy-terms" element={<Privacy />} />
                  <Route path="product" element={<ProductPage />}>
                    <Route path=":sku" element={<ProductPage />} />
                  </Route>
                  <Route path="blog" element={<ShopBlogDetails />}>
                    <Route path=":blogID" element={<ShopBlogDetails />} />
                  </Route>
                  <Route path="blog-explorer" element={<ShopBlogExplorer />} />
                  {/* <Route path=":store" element={<Store />} /> */}
                  <Route path=":contentURL" element={<ContentPage />} />
                  <Route path="store/:store" element={<Store />} />
                  <Route path="cta/:store" element={<Store />} />
                  <Route path="category" element={<ProductList />} />
                  {/* <Route path="faq" element={<FAQ />} /> */}
                  <Route path="contact-us" element={<Contactus />} />
                  <Route
                    path=""
                    element={
                      // eslint-disable-next-line no-nested-ternary
                      data === 'crwdmarket' ? (
                        <ProductList />
                      ) : data === 'token' ? (
                        <TokenLanding />
                      ) : (
                        <ShopHomePage />
                      )
                    }
                  >
                    <Route
                      path="?openSku=:openSku"
                      element={<ShopHomePage />}
                    />
                  </Route>

                  <Route path="*" element={<ErrorPage />} />
                </Routes>
              </BrowserRouter>
            </CartProvider>
          </Provider>
        </Auth0Provider>
      </Elements>
    </>
  );
}
